import { Check, Sparkles } from "lucide-react";

interface PlanFeature {
	text: string;
}

const FREE_FEATURES: PlanFeature[] = [
	{ text: "Unlimited local recordings" },
	{ text: "Screen + webcam + mic + system audio" },
	{ text: "Timeline editor with manual zoom (5 regions)" },
	{ text: "1080p MP4 & GIF export" },
	{ text: "10 background wallpapers" },
	{ text: "Browser device frame" },
	{ text: "Built-in annotations" },
	{ text: "Cursor spotlight & click ripple" },
	{ text: '"Made with Clapio" outro on exports' },
];

const PRO_FEATURES: PlanFeature[] = [
	{ text: "Everything in Free" },
	{ text: "All on-device AI: captions, karaoke captions, transcription (16 languages)" },
	{ text: "Filler-word & silence removal" },
	{ text: "4K / source-quality MP4 export" },
	{ text: "Original-size GIF export" },
	{ text: "Auto-zoom on click + smooth cursor motion" },
	{ text: "Unlimited zoom regions" },
	{ text: "All device frames (MacBook, iPhone, iPad)" },
	{ text: "All 18 wallpapers & custom backgrounds" },
	{ text: 'Remove the "Made with Clapio" outro' },
	{ text: "Free updates — no subscription, ever" },
];

export function Pricing() {
	return (
		<section id="pricing" className="py-24 md:py-32">
			<div className="mx-auto max-w-5xl px-6">
				<div className="text-center mb-16">
					<div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.06] text-xs text-white/40 mb-4">
						<Sparkles size={12} className="text-brand-400" />
						Pricing
					</div>
					<h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">
						Free to use. <span className="gradient-text">One payment to own it all.</span>
					</h2>
					<p className="text-white/40 mt-4 text-base md:text-lg max-w-xl mx-auto">
						No subscription. All AI runs on your machine, so there's nothing to meter — pay once and
						every feature is yours forever.
					</p>
				</div>

				<div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
					{/* Free plan */}
					<div className="relative rounded-2xl p-[1px] bg-white/[0.06]">
						<div className="rounded-2xl p-6 md:p-8 h-full bg-[#0e0e10]">
							<h3 className="text-lg font-bold mb-1">Free</h3>
							<p className="text-sm text-white/40 mb-5">
								A real free plan, not a trial. Record and edit forever.
							</p>

							<div className="mb-6">
								<div className="flex items-baseline gap-1">
									<span className="text-4xl font-extrabold tracking-tight">$0</span>
									<span className="text-sm text-white/30">forever</span>
								</div>
							</div>

							<a
								href="#download"
								className="block text-center py-3 rounded-xl font-semibold text-sm transition-all bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] text-white/80"
							>
								Download Free
							</a>

							<div className="mt-6 space-y-3">
								{FREE_FEATURES.map((f) => (
									<div key={f.text} className="flex items-start gap-2.5">
										<Check size={16} className="text-brand-400 mt-0.5 shrink-0" />
										<span className="text-sm text-white/60">{f.text}</span>
									</div>
								))}
							</div>
						</div>
					</div>

					{/* Pro plan */}
					<div className="relative rounded-2xl p-[1px] bg-gradient-to-b from-brand-400/40 to-brand-600/10">
						<div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-brand-500 text-[10px] font-semibold uppercase tracking-wide">
							Pay once, own forever
						</div>
						<div className="rounded-2xl p-6 md:p-8 h-full bg-[#0c0f0e]">
							<h3 className="text-lg font-bold mb-1">Clapio Pro</h3>
							<p className="text-sm text-white/40 mb-5">
								Everything unlocked — including all on-device AI.
							</p>

							<div className="mb-6">
								<div className="flex items-baseline gap-2">
									<span className="text-4xl font-extrabold tracking-tight">$59</span>
									<span className="text-lg text-white/25 line-through">$79</span>
									<span className="text-sm text-white/30">one-time</span>
								</div>
								<p className="text-xs text-brand-400 mt-1">
									Founder price — lifetime license, free updates
								</p>
							</div>

							<a
								href="#download"
								className="block text-center py-3 rounded-xl font-semibold text-sm transition-all bg-brand-500 hover:bg-brand-400 text-white shadow-lg shadow-brand-500/20"
							>
								Get Clapio Pro
							</a>

							<div className="mt-6 space-y-3">
								{PRO_FEATURES.map((f) => (
									<div key={f.text} className="flex items-start gap-2.5">
										<Check size={16} className="text-brand-400 mt-0.5 shrink-0" />
										<span className="text-sm text-white/60">{f.text}</span>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>

				<p className="text-center text-xs text-white/20 mt-8">
					One-time payment · Secure checkout · Works on macOS, Windows & Linux
				</p>
			</div>
		</section>
	);
}
